import store from "./store.js";
import { groupBy } from "./utility.js";
import { getBoardHostname } from "./appUtility.js";

function resolveTag(system, tag) {
  return (typeof tag == "string") ? system.$atPath(tag) : tag;
}

function getBottomLevelColumns(columns) {
  let subColumns = columns.flatMap(col => col.subColumns);
  while (subColumns.length > 0) {
    columns = subColumns;
    subColumns = subColumns.flatMap(col => col.subColumns);
  }
  return columns;
}

function resolveColumn(session, system, column) {
  let tag = resolveTag(system, column.tag);
  let resolvedColumn = {
    // session, -- see property below
    // system,  -- see property below
    tag,
    name: column.name || tag.name?.value,
    colSpan: Math.max(1, getBottomLevelColumns([column]).length),
    subColumns: resolveColumns(session, system, column.subColumns),
    width: column.width
  };
  Object.defineProperty(resolvedColumn, "session", { value: session });
  Object.defineProperty(resolvedColumn, "system", { value: system });
  return resolvedColumn;
};

function resolveColumns(session, system, columns) {
  return columns.map(column => resolveColumn(session, system, column));
}

function resolveComponentType(componentType) {
  return componentType;
}

// Resolves string-type tag references in the table data to the actual tags,
// sets column widths, adds ids to each row and cell, and filters out rows
// with no relevant cells.
export function resolveTable(session, system, tableData, ignoreRelevance)
{
  let columns = resolveColumns(session, system, tableData.columns);
  let bottomLevelColumns = getBottomLevelColumns(columns);
  let rowNameCounters = {};
  let resolved = {
    // session, -- see property below
    // system,  -- see property below
    tableName: tableData.tableName,
    rowSorts: tableData.rowSorts?.map(rowSort => resolveTag(system, rowSort).value) || [],
    columns,
    rows: tableData.rows.map(row => {
      let cells = row.cells.map((cell, columnIndex) => {
        if (cell == null)
          return null;

        let tag = resolveTag(system, cell.componentArgs.tag);
        console.assert(tag != null);
        
        let enabled = tag.settingEnabled;
        let optional = tag.settingOptional;
        let relevant = (cell.relevant ?? true) || ignoreRelevance;
        let showingAdvanced = store.showAdvanced;
        let showCell = (
          (!cell.advanced || showingAdvanced) &&
          ((enabled && relevant) || (optional && showingAdvanced))
        );
        if (!showCell)
          return null;
        
        let keyHint = cell.keyHint || '';
        return Object.assign({}, cell, {
          tag,
          columnTag: bottomLevelColumns[columnIndex].tag,
          cellId: `${session.sessionId}-${tag.handle}-${keyHint}`,
          componentType: resolveComponentType(cell.componentType),
          componentEventHandlers: cell.componentEventHandlers,
          componentArgs: Object.assign({}, cell.componentArgs, { tag })
        });
      });
      let createRowId = (row) => {
        rowNameCounters[row.rowName] = (rowNameCounters[row.rowName] ?? 0) + 1;
        return row.id ?? `${row.rowName} [${rowNameCounters[row.rowName]}]`;
      };
      return Object.assign({}, row, {
        id: createRowId(row),
        rowName: row.rowName,
        templateIndex: row.templateIndex,
        numRelevantCells: cells.filter(cell => cell != null).length,
        cells
      });
    }).filter(row => row.cells.some(cell => cell != null))
  };
  Object.defineProperty(resolved, "session", { value: session });
  Object.defineProperty(resolved, "system", { value: system });
  
  resolved.rowSorts.forEach(rowSort => {
    resolved.rows = sortRows(resolved.rows, rowSort);
  });
  
  return resolved;
}

function combineColumns(tables, showSystemColumnsWhenMerged) {
  let columns = [];
  let allBins = store.sessions.filter(session => session.system).flatMap(session => session.system?.bins.children);
  let isSystemTag = (tag) => !tag.$parents().some(parent => allBins.includes(parent));
  let alreadyHasSystemColumns = tables.some(table => table.columns[0].tag == store.sessions[0].system);
  let addSystemColumns = !alreadyHasSystemColumns && (tables.length > 1 || tables[0].rows.some(row => row.cells.some(cell => cell?.tag && isSystemTag(cell.tag))));
  if (addSystemColumns) {
    // Add a top-level column group for labelling systems.
    columns = tables.map(table => {
      let bottomLevel = table.columns.flatMap(column => getBottomLevelColumns([column]));
      return resolveColumn(table.session, table.system, {
        tag: table.system,
        name: table.system.boardName.value,
        colSpan: bottomLevel.length,
        subColumns: table.columns
      });
    });
  }
  else {
    columns = tables.flatMap(table => table.columns);
  }
  
  let sessions = store.sessions.filter(session => session.system != null);
  let systems = sessions.map(session => session.system);
  let hasOnlySingleBinSystems = systems.every(system => system.bins.children.length == 1);
  let shouldRemoveBinColumns = systems.length > 1 && hasOnlySingleBinSystems;
  if (shouldRemoveBinColumns) {
    let subColumns = columns.flatMap(column => column.subColumns);
    let areBinsInFirstColumnGroup = columns.some(column => allBins.includes(column.tag));
    let areBinsInSecondColumnGroup = subColumns.some(subColumn => allBins.includes(subColumn.tag));
    if (areBinsInFirstColumnGroup) {
      columns = subColumns;
    }
    else if (areBinsInSecondColumnGroup) {
      columns.forEach(column => {
        column.subColumns = column.subColumns[0].subColumns;
      });
    }
  }
  else {
    // Hide bin names for single bin systems if the name isn't unique.
    let singleBinSystems = systems.filter(system => system.bins.children.length == 1);
    let singleBins = singleBinSystems.flatMap(system => system.bins.children);
    singleBins.forEach(singleBin => {
      let numBinsWithSameName = allBins.filter(bin => bin.name.value == singleBin.name.value).length;
      if (numBinsWithSameName > 1) {
        let columnsToSearch = [ ... columns, ... columns.flatMap(column => column.subColumns) ];
        let column = columnsToSearch.find(column => column.tag == singleBin);
        if (column)
          column.name = "";
      }
    });
  }

  let setColumnWidths = (columns) => {
    let subColumns = columns.flatMap(column => column.subColumns);
    columns.forEach(column => {
      if (column.subColumns.length > 0) {
        setColumnWidths(subColumns);
        column.width = `${column.subColumns.reduce((sum, subColumn) => sum + parseFloat(subColumn.width), 0)}%`;
      }
      else {
        column.width = `${100 / columns.length}%`;
      }
    });
  };
  setColumnWidths(columns);
  
  return columns;
}

// Combines two or more single-system table instances.
export function combineTables(tables, showSystemColumnsWhenMerged = true)
{
  let tablesSorted = tables.slice().sort((t1, t2) => {
    let boardsOnSite = store.sessions[0].system.boardsOnSite.children;
    let getSortIndex = (table) => boardsOnSite.findIndex(board =>
      board.macAddress.value == table.session.system.macAddr.value ||
      getBoardHostname(board) == table.session.hostname
    );
    return getSortIndex(t1) - getSortIndex(t2);
  });
  
  let combinedTable = {
    tableName: tablesSorted[0].tableName,
    rowSorts: tablesSorted[0].rowSorts,
    columns: combineColumns(tablesSorted, showSystemColumnsWhenMerged),
    rows: []
  };
  let columnCounter = 0;
  let bottomLevelColumns = getBottomLevelColumns(combinedTable.columns);
  let numColumns = bottomLevelColumns.length;
  tablesSorted.forEach(table => {
    table.rows.forEach(incomingRow => {
      // Search for any row already in 'combinedTable' that matches the incoming row, ignoring 
      // rows in 'combinedTable' that don't have any cells available for the incoming row's cells.
      let matchingNames = (row1, row2) => row1.rowName == row2.rowName;
      let nonOverlappingCells = (row1, row2) => row1.cells.slice(columnCounter, columnCounter + row2.cells.length).every((cell, index) => cell == null || row2.cells[index] == null);
      let combinedRow = combinedTable.rows.find(row => 
        matchingNames(row, incomingRow) && 
        nonOverlappingCells(row, incomingRow)
      );
      // Insert a new row in 'combinedTable' if no match was found.
      if (combinedRow == null) {
        let insertAt = combinedTable.rows.findIndex(row => incomingRow.templateIndex < row.templateIndex);
        if (insertAt == -1) insertAt = combinedTable.rows.length;
        combinedTable.rows.splice(insertAt, 0, {
          id: incomingRow.id,
          rowName: incomingRow.rowName,
          templateIndex: incomingRow.templateIndex,
          numRelevantCells: 0, // See below
          cells: [ ... new Array(numColumns) ]
        });
        combinedRow = combinedTable.rows[insertAt];
      }
      // Write the incoming row's cells into the combined row.
      incomingRow.cells.forEach((cell, index) => {
        if (combinedRow.cells[columnCounter + index] == null)
          combinedRow.cells[columnCounter + index] = cell;
      });
      combinedRow.numRelevantCells += incomingRow.numRelevantCells;
    });
    columnCounter += getBottomLevelColumns(table.columns).length;
  });
  
  // Sort the rows if required.
  combinedTable.rowSorts.forEach(sortOrder => {
    combinedTable.rows = sortRows(combinedTable.rows, sortOrder);
  });

  return combinedTable;
}

export function createMultiSystemTable(tableKey, showSystemColumnsWhenMerged = true) {
  let connectedSessions = store.sessions.filter(session => session.isConnected);
  let tables = connectedSessions.map(session => {
    let tableData = session.system.tables[tableKey].value;
    return resolveTable(session, session.system, tableData);
  });
  return combineTables(tables, showSystemColumnsWhenMerged);
}

export function createSingleColumnTableData(tableData, columnIndex) 
{
  return {
    tableName: tableData.tableName,
    columns: [ tableData.columns[columnIndex] ],
    rows: tableData.rows.map(row => {
      return {
        rowName: row.rowName,
        templateIndex: row.templateIndex,
        cells: [ row.cells[columnIndex] ]
      };
    }).filter(row => row.cells[0] != null)
  };
}

export function resolveTableTemplate(session, system, tableTemplate)
{
  let columnTags = system.$atPath(tableTemplate.columnsPath, true);
  let tableData = {
    tableName: tableTemplate.tableName,
    columns: columnTags.map(tag => ({ 
      tag,
      name: null,
      subColumns: [],
      width: `${100 / columnTags.length}%`
    })),
    rows: tableTemplate.rowTemplates.flatMap((rowTemplate, templateIndex) => {
      // auxiliaries[].temperature ->
      //   bins[0].auxiliaries[0].temperature
      //   bins[0].auxiliaries[1].temperature
      //   bins[1].auxiliaries[0].temperature
      let tags = columnTags.flatMap(columnTag => {
        return columnTag.$atPath(rowTemplate.tag);
      });
      let tagsByRowName = groupBy(tags, tag => {
        if (rowTemplate.rowName.includes("{#}")) {
          let auxiliary = [ ... tag.$parents(), tag ].reverse().find(tag => !isNaN(tag.$key()));
          return rowTemplate.rowName.replace("{#}", auxiliary.$key() + 1);
        }
        else {
          return rowTemplate.rowName;
        }
      });
      return Object.keys(tagsByRowName).flatMap(rowName => {
        let tags = tagsByRowName[rowName];
        let row = {
          rowName,
          templateIndex,
          cells: columnTags.map(columnTag => {
            let tag = tags.find(tag => tag.$parents().includes(columnTag));
            if (tag == null) return null;
            return {
              componentType: rowTemplate.cellComponentType,
              componentArgs: {
                ...rowTemplate.cellComponentArgs,
                tag
              }
            };
          })
        };
        return row;
      });
    })
  };
  return resolveTable(session, system, tableData, true);
}

// Rows with no corresponding entry in the ordering will retain their original position.
export function sortRows(rows, orderedRowNames) {
  let result = rows.map(row => {
    let hasSpecifiedOrder = orderedRowNames.find(partialName => row.rowName.startsWith(partialName));
    if (hasSpecifiedOrder)
      return null;
    else
      return row;
  });
  orderedRowNames.forEach(partialName => {
    let matchingRows = rows.filter(row => row.rowName.startsWith(partialName));
    matchingRows.forEach(row => {
      let nextNullIndex = result.indexOf(null);
      result[nextNullIndex] = row;
    });
  });
  return result;
}